import { CheckCircle, CircleUser, KeyRound, Mail, Shield, UserCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { useAuthStore } from '@/core/stores/authStore';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { SectionHeader } from '@/shared/components/ui/header';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { getRoleLabel } from '@/shared/utils/role-label.utils';

export default function ProfilePage() {
  const user = useAuthStore((s) => s.user);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!showSuccess) return;
    const timer = setTimeout(() => setShowSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [showSuccess]);

  const handleSuccess = () => setShowSuccess(true);

  const initials = user?.displayName
    ? `${user.displayName?.[0] ?? ''}${user.displayName?.split(' ')[1]?.[0] ?? ''}`.toUpperCase()
    : 'US';

  return (
    <div className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<CircleUser />}
        title="Mi perfil"
        subtitle="Información personal y de seguridad de tu cuenta"
      >
        <UnifiedButton
          buttonType="basic"
          variant="outline"
          onClick={() => setIsPasswordModalOpen(true)}
          data-testid="profile-change-password-button"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          Cambiar contraseña
        </UnifiedButton>
      </SectionHeader>

      {showSuccess && (
        <div
          className="flex items-center gap-2 p-3 rounded-md bg-positive/10 border border-positive/30"
          data-testid="profile-password-success"
        >
          <CheckCircle className="w-4 h-4 text-positive" />
          <p className="text-sm text-positive">
            Contraseña actualizada correctamente. Te enviamos un correo de confirmación.
          </p>
        </div>
      )}

      <div className="rounded-xl overflow-hidden bg-background border border-outline">
        <div className="px-5 py-4 flex items-center gap-2 border-b border-outline bg-select-background">
          <h3 className="text-[0.875rem] font-semibold text-primary tracking-[0.08em]">
            Información general
          </h3>
        </div>

        <div className="p-6 flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16 text-lg">
              <AvatarFallback>{initials}</AvatarFallback>
              {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="avatar" />}
            </Avatar>
            <div className="flex flex-col">
              <span className="text-light text-base font-medium">
                {user?.displayName || 'Usuario'}
              </span>
              <span className="text-primary text-sm">{user?.email || '—'}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Input
              label="ID de usuario"
              value={user?.userId ?? ''}
              iconStart={<CircleUser size={16} />}
              readOnly
              disabled
              data-testid="profile-info-id-value"
            />
            <Input
              label="Nombre"
              value={user?.displayName ?? ''}
              iconStart={<UserCircle2 size={16} />}
              readOnly
              disabled
              data-testid="profile-info-name-value"
            />
            <Input
              label="Correo electrónico"
              value={user?.email ?? ''}
              iconStart={<Mail size={16} />}
              readOnly
              disabled
              data-testid="profile-info-email-value"
            />
            <Input
              label="Rol"
              value={getRoleLabel(user?.role?.name) || 'Sin rol asignado'}
              iconStart={<Shield size={16} />}
              readOnly
              disabled
              data-testid="profile-info-role-value"
            />
          </div>
        </div>
      </div>

      <ChangePasswordModal
        open={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
